import { AppColors, AppPalette, CustomPalette } from "@bpipartners/webui";

const appPalette: AppPalette = {
  primary: {
    "0": "#000000",
    "5": "#090F01",
    "10": "#111E01",
    "20": "#223D03",
    "25": "#2B4C04",
    "30": "#335B04",
    "35": "#3C6A05",
    "40": "#447A06",
    "50": "#559807",
    "60": "#77AD39",
    "70": "#99C16A",
    "80": "#BBD69C",
    "90": "#DDEACD",
    "95": "#EEF5E6",
    "98": "#F8FBF5",
    "99": "#FCFDFA",
    "100": "#FFFFFF",
  },
  secondary: {
    "0": "#000000FF",
    "5": "#050E10",
    "10": "#0A1D20",
    "20": "#143A40",
    "25": "#194850",
    "30": "#1D565F",
    "35": "#22656F",
    "40": "#27737F",
    "50": "#31909F",
    "60": "#5AA6B2",
    "70": "#83BCC5",
    "80": "#ADD3D9",
    "90": "#D6E9EC",
    "95": "#EBF4F6",
    "98": "#F7FBFB",
    "99": "#FBFDFD",
    "100": "#FFFFFFFF",
  },
  tertiary: {
    "0": "#000000FF",
    "5": "#02070A",
    "10": "#050F13",
    "20": "#091E27",
    "25": "#0C2531",
    "30": "#0E2C3A",
    "35": "#103444",
    "40": "#123B4E",
    "50": "#174A61",
    "60": "#456E81",
    "70": "#7492A0",
    "80": "#A2B7C0",
    "90": "#D1DBDF",
    "95": "#E8EDEF",
    "98": "#F6F8F9",
    "99": "#FAFBFC",
    "100": "#FFFFFFFF",
  },
  neutralVariant: {
    "0": "#07000EFF",
    "5": "#0F0D13FF",
    "10": "#262626FF",
    "20": "#444047FF",
    "25": "#3B383EFF",
    "30": "#4C4850FF",
    "35": "#5C5761FF",
    "40": "#686669FF",
    "50": "#717171FF",
    "60": "#888888FF",
    "70": "#B5B3B7FF",
    "80": "#CFCDD0FF",
    "90": "#E6E6E7FF",
    "95": "#F7F7F7FF",
    "98": "#FAF9FCFF",
    "99": "#FDFCFEFF",
    "100": "#FFFFFFFF",
  },

  neutral: {
    "0": "#000000",
    "5": "#0D1012",
    "10": "#181C1E",
    "20": "#2D3235",
    "25": "#373C3F",
    "30": "#41464A",
    "35": "#4B5054",
    "40": "#555A5E",
    "50": "#6A6F73",
    "60": "#7F8488",
    "70": "#94999D",
    "80": "#A9AEB2",
    "90": "#BEC3C7",
    "95": "#D6DBDF",
    "98": "#EFF4F7",
    "99": "#F7FAFC",
    "100": "#FFFFFF",
  },

  error: {
    "0": "#000000",
    "5": "#2D0001",
    "10": "#410002",
    "20": "#690005",
    "25": "#7E0007",
    "30": "#93000A",
    "35": "#A80710",
    "40": "#BA1A1A",
    "50": "#DE3730",
    "60": "#FF5449",
    "70": "#FF897D",
    "80": "#FFB4AB",
    "90": "#FFDAD6",
    "95": "#FFEDEA",
    "98": "#FFF8F7",
    "99": "#FFFBFF",
    "100": "#FFFFFF",
  },
};

const appColors: AppColors = {
  primary: appPalette.primary[40],
  onPrimary: appPalette.primary[100],
  primaryContainer: appPalette.primary[90],
  onPrimaryContainer: appPalette.primary[10],
  secondary: appPalette.secondary[40],
  onSecondary: appPalette.secondary[100],
  secondaryContainer: appPalette.secondary[90],
  onSecondaryContainer: appPalette.secondary[10],
  tertiary: appPalette.tertiary[40],
  onTertiary: appPalette.tertiary[100],
  tertiaryContainer: appPalette.tertiary[90],
  onTertiaryContainer: appPalette.tertiary[10],
  error: appPalette.error[40],
  onError: appPalette.error[100],
  errorContainer: appPalette.error[90],
  onErrorContainer: appPalette.error[10],
  inversePrimary: appPalette.primary[80],
  primaryFixed: appPalette.primary[90],
  onPrimaryFixed: appPalette.primary[10],
  primaryFixedDim: appPalette.primary[80],
  onPrimaryFixedVariant: appPalette.primary[30],
  secondaryFixed: appPalette.secondary[90],
  onSecondaryFixed: appPalette.secondary[10],
  secondaryFixedDim: appPalette.secondary[80],
  onSecondaryFixedVariant: appPalette.secondary[30],
  tertiaryFixed: appPalette.tertiary[90],
  onTertiaryFixed: appPalette.tertiary[10],
  tertiaryFixedDim: appPalette.tertiary[80],
  onTertiaryFixedVariant: appPalette.tertiary[30],
  background: appPalette.neutral[98],
  onBackground: appPalette.neutral[5],
  outline: appPalette.neutral[50],
  outlineVariant: appPalette.neutral[80],
  shadow: appPalette.primary[0],
  scrim: appPalette.primary[0],
  inverseSurface: appPalette.neutralVariant[90],
  inverseOnSurface: appPalette.neutralVariant[10],
  surface: appPalette.neutral[95],
  onSurface: appPalette.neutral[10],
  surfaceVariant: appPalette.neutralVariant[95],
  onSurfaceVariant: appPalette.neutralVariant[10],
  surfaceDim: appPalette.neutral[70],
  surfaceBright: appPalette.neutral[100],
  surfaceContainerLowest: appPalette.neutral[98],
  surfaceContainerLow: appPalette.neutral[95],
  surfaceContainer: appPalette.neutral[90],
  surfaceContainerHigh: appPalette.neutral[80],
  surfaceContainerHighest: appPalette.neutral[70],
  backdrop: appPalette.primary[30],

  relation: { organization: "#0B79D0", person: "#C77700", tag: "#C51162" },
  planning: {},

  importance: {
    major: { fg: "#d82e18", bg: "#f2bdb4" },
    medium: { fg: "#db7c2e", bg: "#EAAF71" },
    normal: { fg: "#6b6f73", bg: "#edf1f4" },
    low: { fg: "#01a2fa", bg: "#c6e9ff" },
  },
  surfaceTint: "",
  timesheet: {
    green: "",
    orange: "",
  },
  customColors: {
    raportTableColors: {
      domain: appPalette.secondary[95],
      goal: appPalette.secondary[98],
      measurement: appPalette.secondary[99],
    },
    measurementLevels: {
      1: "#d82e18",
      2: "#db7c2e",
      3: "#CCA400",
      4: "#8FB302",
      5: "#3D9901",
    },
  },
};

export const colors: CustomPalette = {
  appColors,
  appPalette,
  palette: {
    background: {
      default: appColors.background,
      paper: appColors.surfaceBright,
    },
    primary: {
      light: appColors.primary,
      main: appPalette.primary[60],
      dark: appColors.primary,
      contrastText: appColors.onPrimary,
    },
    secondary: {
      light: appColors.secondary,
      main: appPalette.secondary[60],
      dark: appColors.secondary,
      contrastText: appColors.onSecondary,
    },
  },
};
